import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Community } from '@/mocks/communities';
import ShareModal from './ShareModal';
import ReportModal from './ReportModal';
import GlowButton from '@/components/base/GlowButton';

interface CommunityHeroProps {
  community: Community;
}

const activityConfig = {
  high: { label: 'High Activity', color: 'text-emerald-600', dot: 'bg-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  medium: { label: 'Medium Activity', color: 'text-amber-600', dot: 'bg-amber-500', bg: 'bg-amber-500/10 border-amber-500/30' },
  low: { label: 'Low Activity', color: 'text-slate-400', dot: 'bg-slate-400', bg: 'bg-slate-400/10 border-slate-400/30' },
};

export default function CommunityHero({ community }: CommunityHeroProps) {
  const activity = activityConfig[community.activityLevel];
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();

  const formatCount = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toString();
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <>
      <div className="relative w-full overflow-hidden rounded-2xl border border-white/80 bg-white/50 backdrop-blur-md">
        {/* Cover Image */}
        <div className="relative w-full h-52 md:h-64 overflow-hidden">
          <img
            src={community.coverImage}
            alt={community.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-white/20 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-white/30 via-transparent to-transparent"></div>

          {/* Report button — top right of cover */}
          <button
            onClick={() => setReportOpen(true)}
            className="absolute top-3 right-3 flex items-center gap-1.5 text-[11px] text-slate-500 bg-white/60 hover:bg-white/90 backdrop-blur-sm border border-white/70 px-2.5 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-flag-2-line text-xs w-3 h-3 flex items-center justify-center"></i>
            Report
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 -mt-10 relative">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {/* Avatar */}
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-4 border-white shadow-lg shrink-0 bg-white">
              <img
                src={community.avatar}
                alt={`${community.name} avatar`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Name + badges */}
            <div className="flex-1 min-w-0 pb-1">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className="text-[10px] font-semibold text-[#0088CC] bg-[#0088CC]/10 px-2.5 py-0.5 rounded-full uppercase tracking-wider border border-[#0088CC]/20">
                  {community.category}
                </span>
                {community.featured && (
                  <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200/60">
                    <i className="ri-star-fill text-[9px]"></i>
                    Featured
                  </span>
                )}
                <span className={`flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-0.5 rounded-full border ${activity.bg} ${activity.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${activity.dot}`}></span>
                  {activity.label}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 leading-tight">
                {community.name}
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                <i className="ri-global-line mr-1"></i>{community.region}
                {community.language && (
                  <><span className="mx-2 text-slate-300">·</span><i className="ri-translate-2 mr-1"></i>{community.language}</>
                )}
                <span className="mx-2 text-slate-300">·</span>
                <i className="ri-calendar-line mr-1"></i>Since {formatDate(community.createdAt)}
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-[#c5d9eb]/50 p-3 text-center">
              <div className="text-xl font-extrabold text-[#0088CC]">{formatCount(community.memberCount)}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Members</div>
            </div>
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-[#c5d9eb]/50 p-3 text-center">
              <div className="text-xl font-extrabold text-slate-700">
                {community.weeklyMessages ? formatCount(community.weeklyMessages) : '—'}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">Weekly Msgs</div>
            </div>
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-[#c5d9eb]/50 p-3 text-center">
              <div className="text-xl font-extrabold text-slate-700">{community.adminCount ?? '—'}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Admins</div>
            </div>
          </div>

          {/* Tags — clickable */}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {community.tags.map((tag) => (
              <button
                key={tag}
                onClick={() => navigate(`/tag/${tag}`)}
                className="text-xs text-slate-500 hover:text-[#0088CC] bg-slate-100/80 hover:bg-[#0088CC]/10 border border-slate-200/60 hover:border-[#0088CC]/30 px-2.5 py-1 rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                #{tag}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <GlowButton as="a" href={community.telegramLink} target="_blank" rel="nofollow noopener noreferrer" className="flex-1 text-base font-bold py-3.5 rounded-xl">
              <i className="ri-telegram-fill text-xl"></i>
              Join Community on Telegram
            </GlowButton>
            <GlowButton
              onClick={() => setShareOpen(true)}
              className="px-5 py-3.5 rounded-xl text-sm font-semibold"
              variant="secondary"
            >
              <i className="ri-share-line w-4 h-4 flex items-center justify-center"></i>
              Share
            </GlowButton>
            <GlowButton
              onClick={() => setSaved(!saved)}
              className="px-5 py-3.5 rounded-xl text-sm font-semibold"
              variant={saved ? 'primary' : 'secondary'}
            >
              <i className={`${saved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} w-4 h-4 flex items-center justify-center`}></i>
              {saved ? 'Saved' : 'Save'}
            </GlowButton>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ShareModal
        open={shareOpen}
        communityName={community.name}
        communityId={community.id}
        onClose={() => setShareOpen(false)}
      />
      <ReportModal
        open={reportOpen}
        communityName={community.name}
        onClose={() => setReportOpen(false)}
      />
    </>
  );
}
